import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { tablesService, Table as RTable } from '../services/tables';
import { adminRestaurantsService } from '../services/adminRestaurants';
import { httpClient } from '../services/http';
import { API_ENDPOINTS } from '../config/api';
import { restaurantService } from '../services/restaurants';

type TableNode = { id: string; xPct: number; yPct: number };

interface ClientTablesReservationModalProps {
  visible: boolean;
  onClose: () => void;
  restaurantId: string;
  restaurantName?: string;
  onReservationCreated?: (message: string) => void;
}

export default function ClientTablesReservationModal({
  visible,
  onClose,
  restaurantId,
  restaurantName,
  onReservationCreated,
}: ClientTablesReservationModalProps) {
  const { user } = useAuth();
  const [restaurant, setRestaurant] = useState<any>(null);
  const [tables, setTables] = useState<RTable[]>([]);
  const [layout, setLayout] = useState<TableNode[]>([]);
  const [selectedAreaKey, setSelectedAreaKey] = useState<'all' | 'interior' | 'exterior' | 'barra' | 'vip' | 'terraza'>('all');
  const [isMobileView, setIsMobileView] = useState(false);

  const [reservationTable, setReservationTable] = useState<RTable | null>(null);
  const [showReservationModal, setShowReservationModal] = useState(false);
  const [reservationDate, setReservationDate] = useState<string>('');
  const [reservationTime, setReservationTime] = useState<string>('');
  const [reservationError, setReservationError] = useState<string>('');
  const [reservationPartySize, setReservationPartySize] = useState<number>(2);
  const [selectionId, setSelectionId] = useState<string | null>(null);
  const [selectionLoading, setSelectionLoading] = useState(false);
  const [reservationSubmitting, setReservationSubmitting] = useState(false);
  const [reservationDuration, setReservationDuration] = useState<number>(90);

  const acceptsReservations = Boolean(
    restaurant?.acceptReservations === true ||
    restaurant?.accept_reservations === true
  );

  const canReserve = Boolean(user?.uid && acceptsReservations);

  const AREAS: Record<'interior' | 'exterior' | 'barra' | 'vip' | 'terraza', { x: number; y: number; w: number; h: number; label: string }> = {
    interior: { x: 5, y: 5, w: 55, h: 55, label: 'Interior' },
    terraza: { x: 65, y: 5, w: 30, h: 40, label: 'Terraza' },
    barra: { x: 5, y: 65, w: 35, h: 30, label: 'Barra' },
    vip: { x: 45, y: 65, w: 20, h: 30, label: 'VIP' },
    exterior: { x: 70, y: 50, w: 25, h: 45, label: 'Exterior' },
  };

  function normalizeAreaKey(raw: any): 'interior' | 'exterior' | 'barra' | 'vip' | 'terraza' {
    const s = String(raw || '').toLowerCase().trim();
    if (s.includes('terra')) return 'terraza';
    if (s.includes('exter') || s.includes('afuera') || s.includes('patio')) return 'exterior';
    if (s.includes('bar')) return 'barra';
    if (s.includes('vip')) return 'vip';
    if (s.includes('inter')) return 'interior';
    return (['interior', 'exterior', 'barra', 'vip', 'terraza'].includes(s) ? (s as any) : 'interior');
  }

  // Cálculos para vista actual (aprovechar espacio en área enfocada)
  const areaFocus = selectedAreaKey !== 'all';
  const visibleTables = useMemo(() => (areaFocus ? tables.filter(t => normalizeAreaKey(t.location) === selectedAreaKey) : tables), [areaFocus, tables, selectedAreaKey]);
  const dynamicBaseSizePct = areaFocus ? Math.max(6, Math.min(18, Math.round(60 / Math.sqrt(Math.max(1, visibleTables.length))))) : 0;

  const availableAreas = useMemo(() => {
    const set = new Set<string>();
    tables.forEach(t => set.add(normalizeAreaKey(t.location)));
    const preferred: Array<'interior' | 'terraza' | 'barra' | 'vip' | 'exterior'> = ['interior', 'terraza', 'barra', 'vip', 'exterior'];
    return preferred.filter(k => set.has(k));
  }, [tables]);

  const areaOptions = useMemo<ReadonlyArray<string>>(() => {
    // Si aún no tenemos mesas, mantener opciones clásicas (evita selector vacío en el loading/login).
    if (availableAreas.length === 0) {
      return isMobileView
        ? ['interior', 'terraza', 'barra', 'vip', 'exterior']
        : ['all', 'interior', 'terraza', 'barra', 'vip', 'exterior'];
    }
    if (isMobileView) return availableAreas;
    return availableAreas.length > 1 ? ['all', ...availableAreas] : availableAreas;
  }, [availableAreas, isMobileView]);

  useEffect(() => {
    const onResize = () => {
      const mobile = typeof window !== 'undefined' ? window.innerWidth < 640 : false;
      setIsMobileView(mobile);
      // Asegurar que el área seleccionada sea válida cuando cambia a mobile/desktop
      if (mobile) {
        if (selectedAreaKey === 'all' || (availableAreas.length > 0 && !availableAreas.includes(selectedAreaKey as any))) {
          setSelectedAreaKey((availableAreas[0] || 'interior') as any);
        }
      } else {
        if (availableAreas.length > 0 && selectedAreaKey !== 'all' && !availableAreas.includes(selectedAreaKey as any)) {
          setSelectedAreaKey((availableAreas.length > 1 ? 'all' : (availableAreas[0] as any)) as any);
        }
      }
    };
    onResize();
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', onResize);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', onResize);
      }
    };
  }, [selectedAreaKey, tables, availableAreas]);

  // Si cambian las mesas/áreas disponibles (p.ej. al cargar), ajustar selección si ya no aplica
  useEffect(() => {
    if (!visible) return;
    if (availableAreas.length === 0) return;
    if (isMobileView) {
      if (selectedAreaKey === 'all' || !availableAreas.includes(selectedAreaKey as any)) {
        setSelectedAreaKey((availableAreas[0] || 'interior') as any);
      }
    } else {
      if (selectedAreaKey !== 'all' && !availableAreas.includes(selectedAreaKey as any)) {
        setSelectedAreaKey((availableAreas.length > 1 ? 'all' : (availableAreas[0] as any)) as any);
      }
    }
  }, [visible, availableAreas, isMobileView, selectedAreaKey]);

  // Utilidades de fecha/hora para reservas
  const formatDateInput = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  const formatTimeInput = (d: Date) => {
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  };
  const addMinutes = (d: Date, mins: number) => new Date(d.getTime() + mins * 60000);

  const getMinDateForTable = (t: RTable) => {
    const occupied = (t as any).is_occupied === true || (Array.isArray(t.waiters) && t.waiters.length > 0) || (t.status && t.status !== 'active');
    const today = new Date();
    if (occupied) {
      const tomorrow = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
      return formatDateInput(tomorrow);
    }
    return formatDateInput(today);
  };

  const getMinTimeForSelection = (t: RTable, selectedDateStr: string) => {
    const occupied = (t as any).is_occupied === true || (Array.isArray(t.waiters) && t.waiters.length > 0) || (t.status && t.status !== 'active');
    const today = new Date();
    const selectedParts = selectedDateStr.split('-').map(n => parseInt(n, 10));
    if (selectedParts.length !== 3) return '';
    const selectedDate = new Date(selectedParts[0], selectedParts[1] - 1, selectedParts[2]);
    const sameDay = selectedDate.getFullYear() === today.getFullYear() && selectedDate.getMonth() === today.getMonth() && selectedDate.getDate() === today.getDate();
    if (occupied) return '';
    if (sameDay) {
      const min = addMinutes(today, 30);
      min.setMinutes(Math.ceil(min.getMinutes() / 5) * 5, 0, 0);
      return formatTimeInput(min);
    }
    return '';
  };

  const validateReservation = (t: RTable | null, dateStr: string, timeStr: string) => {
    if (!t || !dateStr || !timeStr) return 'Selecciona fecha y hora';
    const minDateStr = getMinDateForTable(t);
    if (dateStr < minDateStr) return 'Fecha inválida';
    const minTimeStr = getMinTimeForSelection(t, dateStr);
    if (minTimeStr && dateStr === formatDateInput(new Date())) {
      if (timeStr < minTimeStr) return `Hora mínima ${minTimeStr}`;
    }
    return '';
  };

  const openReservationForTable = async (t: RTable) => {
    const minDateStr = getMinDateForTable(t);
    const minTimeStr = getMinTimeForSelection(t, minDateStr);
    setReservationTable(t);
    setReservationDate(minDateStr);
    setReservationTime(minTimeStr || '12:00');
    setReservationError('');
    setReservationPartySize(Math.max(1, Number((t as any).capacity) || 2));
    setReservationDuration(90);
    setShowReservationModal(true);
    // Crear selección (bloqueo temporal)
    if (user?.uid && restaurantId) {
      try {
        setSelectionLoading(true);
        const resp: any = await httpClient.post(
          API_ENDPOINTS.RESERVATION_SELECTION(user.uid, restaurantId),
          { restaurantId, tableId: t.id },
          'authenticated'
        );
        setSelectionId(String(resp?.id || resp?.selection_id || ''));
      } catch (e: any) {
        setSelectionId(null);
        const msg = String(e?.message || 'Error al seleccionar la mesa');
        setReservationError(
          msg.includes('seleccionada') || e?.status === 409
            ? 'Alguién te ganó la selección, intenta de nuevo en 5 minutos.'
            : msg.includes('restaurantId')
              ? 'Falta restaurantId para seleccionar. Intenta de nuevo.'
              : msg
        );
      } finally {
        setSelectionLoading(false);
      }
    }
  };

  const closeReservationModal = async () => {
    setShowReservationModal(false);
    // Cancelar selección activa para liberar mesa
    if (user?.uid && restaurantId && selectionId) {
      try {
        await httpClient.delete(
          API_ENDPOINTS.RESERVATION_SELECTION_BY_ID(user.uid, restaurantId, selectionId),
          'authenticated'
        );
      } catch {}
    }
    setSelectionId(null);
    setReservationTable(null);
    setReservationError('');
    setReservationPartySize(2);
    setReservationDuration(90);
  };

  const submitReservation = async () => {
    if (!reservationTable || !user?.uid || !restaurantId) return;
    const err = validateReservation(reservationTable, reservationDate, reservationTime);
    if (err) {
      setReservationError(err);
      return;
    }
    try {
      setReservationSubmitting(true);
      // Validar número de personas
      if (reservationPartySize < 1 || (reservationTable.capacity && reservationPartySize > reservationTable.capacity)) {
        setReservationError(`Número de personas inválido (1-${reservationTable.capacity || 20})`);
        setReservationSubmitting(false);
        return;
      }
      const localIso = new Date(`${reservationDate}T${reservationTime}:00`).toISOString();
      await httpClient.post(
        API_ENDPOINTS.RESERVATIONS(user.uid, restaurantId),
        { restaurantId, tableId: reservationTable.id, reservationDate: localIso, partySize: reservationPartySize, durationMinutes: reservationDuration, selectionId: selectionId || undefined },
        'authenticated'
      );
      onReservationCreated?.('Reservación creada. El restaurante debe confirmarla.');
      await closeReservationModal();
    } catch (e: any) {
      setReservationError(e?.message || 'No se pudo crear la reservación');
    } finally {
      setReservationSubmitting(false);
    }
  };

  const loadRestaurantInfo = async () => {
    if (!restaurantId) return;
    try {
      const restaurantData = await restaurantService.getRestaurantById(restaurantId);
      if (restaurantData) setRestaurant(restaurantData);
    } catch {}
  };

  const loadTablesAndLayout = async () => {
    if (!restaurantId) return;
    // La disponibilidad y reservas requieren sesión (mismo comportamiento que OrdersHistory).
    if (!user?.uid) {
      await loadRestaurantInfo();
      setTables([]);
      setLayout([]);
      return;
    }
    const res = await tablesService.getTables(user.uid, restaurantId);
    const list: RTable[] = Array.isArray(res) ? res : [];
    setTables(list);
    await loadRestaurantInfo();
    try {
      const rest = await adminRestaurantsService.getRestaurantById(user.uid, restaurantId);
      const l = (rest as any)?.onboarding?.tableLayout as Array<{ id: string; xPct: number; yPct: number } | undefined>;
      if (Array.isArray(l)) setLayout(l.filter(Boolean) as any);
      else setLayout([]);
    } catch {
      setLayout([]);
    }
    const mobile = typeof window !== 'undefined' ? window.innerWidth < 640 : false;
    setIsMobileView(mobile);
    const preferredAreas: Array<'interior' | 'terraza' | 'barra' | 'vip' | 'exterior'> = ['interior', 'terraza', 'barra', 'vip', 'exterior'];
    const firstWithTables = preferredAreas.find(a => list.some(t => normalizeAreaKey(t.location) === a)) || 'interior';
    setSelectedAreaKey(mobile ? firstWithTables : 'all');
  };

  useEffect(() => {
    if (!visible) return;
    setReservationTable(null);
    setShowReservationModal(false);
    setReservationError('');
    setSelectionId(null);
    void loadTablesAndLayout();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, restaurantId, user?.uid]);

  if (!visible) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50" onClick={onClose}>
        <div className="bg-white w-full h-full overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#22B6B2]/20 bg-[#e9ecef]">
            <h3 className="text-lg font-bold text-[#2C4358]">{restaurantName || restaurant?.name || ''}</h3>
            <button className="px-3 py-1 rounded-lg bg-[#22B6B2] text-white font-bold" onClick={onClose}>Cerrar</button>
          </div>
          {/* Selector de áreas */}
          <div className="flex items-center gap-2 px-4 py-2 bg-[#f8f9fa] border-b border-[#22B6B2]/10 overflow-x-auto">
            {areaOptions.map((key) => (
              <button
                key={key}
                className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors whitespace-nowrap ${selectedAreaKey === key ? 'bg-[#22B6B2] text-white border-[#22B6B2]' : 'bg-white text-[#2C4358] border-[#22B6B2]/30'}`}
                onClick={() => setSelectedAreaKey(key as any)}
              >
                {key === 'all' ? 'Todas' : AREAS[key as 'interior'].label}
              </button>
            ))}
          </div>
          <div className="relative w-full flex-1">
            {/* Áreas */}
            {selectedAreaKey === 'all' && Object.entries(AREAS).map(([key, area]) => (
              <div key={key} className="absolute border-2 border-dashed border-[#22B6B2]/20" style={{ left: `${area.x}%`, top: `${area.y}%`, width: `${area.w}%`, height: `${area.h}%` }}>
                <div className="absolute -top-6 left-0 px-2 py-0.5 rounded-md bg-[#22B6B2] text-white text-xs font-bold shadow">{area.label}</div>
              </div>
            ))}
            {/* Mesas */}
            {visibleTables.map((t) => {
              const loc = normalizeAreaKey(t.location);
              const areaSelected = AREAS[(areaFocus ? selectedAreaKey : loc) as 'interior'];
              const node = layout.find(n => n.id === t.id);
              const posGlobal = node ? { xPct: node.xPct, yPct: node.yPct } : null;
              // Fallback simple si no hay layout (en área correspondiente)
              const fallbackIndex = tables.filter(tt => normalizeAreaKey(tt.location) === (areaFocus ? selectedAreaKey : loc)).findIndex(tt => tt.id === t.id);
              const fallback = (() => {
                const cols = 3;
                const padding = 5;
                const row = Math.floor(fallbackIndex / cols);
                const col = fallbackIndex % cols;
                const stepX = (areaSelected.w - padding * 2) / (cols - 1);
                const stepY = Math.max(10, (areaSelected.h - padding * 2) / 5);
                return { xPct: areaSelected.x + padding + col * stepX, yPct: areaSelected.y + padding + row * stepY };
              })();

              let leftPct: number;
              let topPct: number;
              if (areaFocus) {
                // Convertir a coordenadas locales del área (0-100%) y usar como left/top del contenedor
                const xRef = (posGlobal?.xPct ?? fallback.xPct);
                const yRef = (posGlobal?.yPct ?? fallback.yPct);
                const localX = Math.min(100, Math.max(0, ((xRef - areaSelected.x) / areaSelected.w) * 100));
                const localY = Math.min(100, Math.max(0, ((yRef - areaSelected.y) / areaSelected.h) * 100));
                leftPct = localX;
                topPct = localY;
              } else {
                // Usar posiciones globales en porcentaje del canvas
                const xRef = posGlobal ? Math.min(areaSelected.x + areaSelected.w, Math.max(areaSelected.x, posGlobal.xPct)) : fallback.xPct;
                const yRef = posGlobal ? Math.min(areaSelected.y + areaSelected.h, Math.max(areaSelected.y, posGlobal.yPct)) : fallback.yPct;
                leftPct = xRef;
                topPct = yRef;
              }
              const isOccupied = (t as any).is_occupied === true || (Array.isArray(t.waiters) && t.waiters.length > 0) || (t.status && t.status !== 'active');
              const img = isOccupied ? '/assets/images/mesitaocupada.png' : '/assets/images/mesaconmesero.png';
              const baseSize = isMobileView ? 50 : 64;
              const scale = (cap?: number) => { const c = Number(cap || 4); if (c <= 2) return 0.8; if (c <= 3) return 0.9; if (c <= 5) return 1.1; if (c <= 7) return 1.2; return 1.3; };
              const capScale = scale((t as any).capacity);
              const sizePx = Math.round(baseSize * capScale);
              const sizePct = areaFocus ? Math.max(5, Math.min(22, Math.round(dynamicBaseSizePct * capScale))) : undefined;
              // Margen visual mínimo entre mesas en vista enfocada (sin mover posiciones)
              const gapPct = areaFocus ? Math.max(0.5, Math.min(3, Math.round((8 / Math.sqrt(Math.max(1, visibleTables.length))) * 10) / 10)) : 0;
              return (
                <div
                  key={t.id}
                  className={`absolute -translate-x-1/2 -translate-y-1/2 select-none ${canReserve ? 'cursor-pointer' : 'cursor-default'}`}
                  style={{ left: `${leftPct}%`, top: `${topPct}%`, width: areaFocus ? `${sizePct}%` : sizePx, height: areaFocus ? `${sizePct}%` : sizePx, padding: areaFocus ? `${gapPct}%` : 0, boxSizing: 'border-box' }}
                  onClick={() => canReserve ? openReservationForTable(t) : undefined}
                  title={
                    !acceptsReservations
                      ? 'Este restaurante no acepta reservaciones'
                      : (!user?.uid ? 'Inicia sesión para reservar' : undefined)
                  }
                >
                  <div className="relative w-full h-full">
                    <img src={img} alt={`Mesa`} className="w-full h-full object-contain" draggable={false} />
                  </div>
                </div>
              );
            })}
            {!acceptsReservations && (
              <div className="absolute inset-x-0 bottom-4 px-4">
                <div className="mx-auto max-w-md rounded-xl border border-gray-200 bg-white/90 px-4 py-3 text-sm text-[#2C4358]">
                  Este restaurante no acepta reservaciones.
                </div>
              </div>
            )}
            {acceptsReservations && !user?.uid && (
              <div className="absolute inset-x-0 bottom-4 px-4">
                <div className="mx-auto max-w-md rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                  Inicia sesión para ver disponibilidad y reservar mesas.
                </div>
              </div>
            )}
          </div>
          <div className="px-4 py-2 bg-[#e9ecef] border-t border-[#22B6B2]/20 text-xs text-[#85929e]">
            {!acceptsReservations
              ? 'Este restaurante no acepta reservaciones.'
              : (user?.uid ? 'Selecciona una mesa para ver su capacidad y reservarla!' : 'Inicia sesión para reservar mesas.')}
          </div>
        </div>
      </div>

      {/* Modal de Reserva */}
      {showReservationModal && reservationTable && (
        <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowReservationModal(false)}>
          <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-200 bg-[#e9ecef] flex items-center justify-between">
              <div>
                <div className="text-xs text-[#85929e]">Reserva de mesa</div>
                <div className="text-lg font-bold text-[#2C4358]">Mesa {reservationTable.number} · Capacidad {reservationTable.capacity}</div>
              </div>
              <button className="px-3 py-1 rounded-lg bg-[#22B6B2] text-white font-bold" onClick={closeReservationModal}>Cerrar</button>
            </div>
            <div className="p-6 space-y-6">
              {/* Calendario/Fecha */}
              <div>
                <label className="block text-sm font-semibold text-[#2C4358] mb-2">Fecha</label>
                <input
                  type="date"
                  className="w-full px-4 py-3 border-2 border-[#22B6B2]/30 rounded-xl focus:outline-none focus:border-[#22B6B2]"
                  min={getMinDateForTable(reservationTable)}
                  value={reservationDate}
                  onChange={(e) => {
                    const newDate = e.target.value;
                    const minDate = getMinDateForTable(reservationTable);
                    const validDate = newDate < minDate ? minDate : newDate;
                    setReservationDate(validDate);
                    const minTime = getMinTimeForSelection(reservationTable, validDate);
                    if (minTime) {
                      setReservationTime((prev) => !prev || prev < minTime ? minTime : prev);
                    }
                    const err = validateReservation(reservationTable, validDate, reservationTime);
                    setReservationError(err);
                  }}
                />
              </div>

              {/* Hora */}
              <div>
                <label className="block text-sm font-semibold text-[#2C4358] mb-2">Hora</label>
                <input
                  type="time"
                  className="w-full px-4 py-3 border-2 border-[#22B6B2]/30 rounded-xl focus:outline-none focus:border-[#22B6B2]"
                  min={getMinTimeForSelection(reservationTable, reservationDate) || undefined}
                  value={reservationTime}
                  onChange={(e) => {
                    const newTime = e.target.value;
                    setReservationTime(newTime);
                    const err = validateReservation(reservationTable, reservationDate, newTime);
                    setReservationError(err);
                  }}
                />
                {(selectionLoading || reservationSubmitting) && (
                  <div className="mt-2 text-sm text-[#2C4358]">Procesando...</div>
                )}
                {reservationError && !selectionLoading && !reservationSubmitting && (
                  <div className="mt-2 text-sm text-red-600">{reservationError}</div>
                )}
              </div>

              {/* Personas */}
              <div>
                <label className="block text-sm font-semibold text-[#2C4358] mb-2">Personas</label>
                <input
                  type="number"
                  min={1}
                  max={reservationTable.capacity || undefined}
                  value={reservationPartySize}
                  onChange={(e) => setReservationPartySize(Math.max(1, Math.min(Number(e.target.value || 1), reservationTable.capacity || Number(e.target.value || 1))))}
                  className="w-full px-4 py-3 border-2 border-[#22B6B2]/30 rounded-xl focus:outline-none focus:border-[#22B6B2]"
                />
                {(reservationPartySize < 1 || (reservationTable.capacity && reservationPartySize > reservationTable.capacity)) && (
                  <div className="mt-2 text-sm text-red-600">Número de personas inválido (1-{reservationTable.capacity || 20})</div>
                )}
              </div>

              {/* Duración */}
              <div>
                <label className="block text-sm font-semibold text-[#2C4358] mb-2">Duración (min)</label>
                <input
                  type="number"
                  min={15}
                  max={240}
                  step={15}
                  value={reservationDuration}
                  onChange={(e) => setReservationDuration(Math.max(15, Math.min(240, Number(e.target.value || 90))))}
                  className="w-full px-4 py-3 border-2 border-[#22B6B2]/30 rounded-xl focus:outline-none focus:border-[#22B6B2]"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 bg-[#f8f9fa] flex gap-3">
              <button
                className="flex-1 px-4 py-3 rounded-xl border border-gray-300 bg-white hover:bg-gray-50 text-[#2C4358] font-semibold"
                onClick={closeReservationModal}
              >
                Cancelar
              </button>
              <button
                className="flex-1 px-4 py-3 rounded-xl bg-[#22B6B2] text-white font-bold disabled:opacity-50"
                disabled={
                  !!validateReservation(reservationTable, reservationDate, reservationTime) ||
                  selectionLoading ||
                  reservationSubmitting ||
                  reservationPartySize < 1 ||
                  ((reservationTable as any)?.capacity !== undefined && (reservationTable as any)?.capacity !== null && reservationPartySize > Number((reservationTable as any).capacity))
                }
                onClick={submitReservation}
              >
                {reservationSubmitting ? 'Creando...' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

